import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Alert from 'Components/Alert';
import FieldSet from 'Components/FieldSet';
import TextInput from 'Components/Form/TextInput';
import Label from 'Components/Label';
import Button from 'Components/Link/Button';
import ConfirmModal from 'Components/Modal/ConfirmModal';
import PageContent from 'Components/Page/PageContent';
import PageContentBody from 'Components/Page/PageContentBody';
import PageToolbar from 'Components/Page/Toolbar/PageToolbar';
import PageToolbarButton from 'Components/Page/Toolbar/PageToolbarButton';
import PageToolbarSection from 'Components/Page/Toolbar/PageToolbarSection';
import Table from 'Components/Table/Table';
import TableBody from 'Components/Table/TableBody';
import TableRow from 'Components/Table/TableRow';
import TableRowCell from '../components/TdarrTableRowCell';
import { align, icons, kinds, sortDirections } from 'Helpers/Props';
import { fileName, formatDate, formatMegabytes } from '../format';
import { tdarrPost } from '../api';
import { useTdarrActivityTable, useTdarrLibraries } from '../queries';
import { TdarrJobRecord, TdarrMediaFile, TdarrStagedFile } from '../types';
import ActivityActionMenu, { ActivityAction } from '../components/ActivityActionMenu';

const pageSize = 25;
const views = [
  { key: 'staging', label: 'Staging Section', type: 'staged' as const },
  { key: 'hold', label: 'On Hold', type: 'status-tables' as const, table: 'table0' },
  { key: 'transcode-queue', label: 'Transcode Queue', type: 'status-tables' as const, table: 'table1' },
  { key: 'transcode-success', label: 'Transcode Success / Not Required', type: 'status-tables' as const, table: 'table2' },
  { key: 'transcode-error', label: 'Transcode Error / Cancelled', type: 'status-tables' as const, table: 'table3' },
  { key: 'health-queue', label: 'Health Check Queue', type: 'status-tables' as const, table: 'table4' },
  { key: 'health-success', label: 'Health Check Healthy', type: 'status-tables' as const, table: 'table5' },
  { key: 'health-error', label: 'Health Check Error / Cancelled', type: 'status-tables' as const, table: 'table6' },
  { key: 'history', label: 'Job History', type: 'jobs' as const }
];

interface ActionRequest {
  key: string;
  label: string;
  path: string;
  data: Record<string, unknown>;
  confirmMessage?: string;
}

const mediaColumns = [
  { name: 'DB', label: 'Library', isVisible: true, isSortable: true }, { name: 'file', label: 'File', isVisible: true, isSortable: true },
  { name: 'video_codec_name', label: 'Video', isVisible: true, isSortable: true }, { name: 'video_resolution', label: 'Resolution', isVisible: true, isSortable: true },
  { name: 'audio_codec_name', label: 'Audio', isVisible: true, isSortable: true }, { name: 'container', label: 'Container', isVisible: true, isSortable: true },
  { name: 'file_size', label: 'Size', isVisible: true, isSortable: true }, { name: 'actions', label: 'Actions', isVisible: true, isSortable: false }
];
const stagedColumns = [
  { name: 'status', label: 'Status', isVisible: true, isSortable: true }, { name: 'originalLibraryFile.file', label: 'File', isVisible: true, isSortable: true },
  { name: 'originalLibraryFile.file_size', label: 'Original', isVisible: true, isSortable: true }, { name: 'lastCompletedCacheFile.file_size', label: 'Output', isVisible: true, isSortable: true },
  { name: 'saved', label: 'Saved', isVisible: true, isSortable: false }, { name: 'workerGenus', label: 'Worker', isVisible: true, isSortable: true },
  { name: 'actions', label: 'Actions', isVisible: true, isSortable: false }
];
const historyColumns = [
  { name: 'job.type', label: 'Type', isVisible: true, isSortable: true }, { name: 'start', label: 'Started', isVisible: true, isSortable: true },
  { name: 'file', label: 'File', isVisible: true, isSortable: true }, { name: 'status', label: 'Status', isVisible: true, isSortable: true },
  { name: 'workerGenus', label: 'Worker', isVisible: true, isSortable: true }, { name: 'nodeNames', label: 'Node', isVisible: true, isSortable: false },
  { name: 'actions', label: 'Actions', isVisible: true, isSortable: false }
];

const statusActions: Record<string, ActivityAction[]> = {
  table0: [{ key: 'unhold', label: 'Unhold' }],
  table1: [{ key: 'bump', label: 'Bump' }, { key: 'deprioritize', label: 'Deprioritize' }, { key: 'unbump', label: 'Reset priority' }, { key: 'skip', label: 'Skip' }],
  table2: [{ key: 'requeue', label: 'Requeue' }],
  table3: [{ key: 'requeue', label: 'Requeue' }, { key: 'ignore', label: 'Ignore' }, { key: 'delete', label: 'Delete file' }],
  table4: [{ key: 'bump', label: 'Bump' }, { key: 'deprioritize', label: 'Deprioritize' }, { key: 'unbump', label: 'Reset priority' }, { key: 'skip', label: 'Skip' }],
  table5: [{ key: 'requeue', label: 'Requeue' }],
  table6: [{ key: 'requeue', label: 'Requeue' }, { key: 'ignore', label: 'Ignore' }, { key: 'delete', label: 'Delete file' }]
};

function getStatusUpdate(table: string, action: string) {
  if (table === 'table0') return { TranscodeDecisionMaker: 'Queued', HealthCheck: 'Queued', holdUntil: 0 };
  if (table === 'table1') {
    if (action === 'bump') return { bumped: true };
    if (action === 'deprioritize') return { bumped: -1 };
    if (action === 'unbump') return { bumped: false };
    return { TranscodeDecisionMaker: 'Not required', lastTranscodeDate: Date.now() };
  }
  if (table === 'table2') return { TranscodeDecisionMaker: 'Queued' };
  if (table === 'table3') return { TranscodeDecisionMaker: action === 'requeue' ? 'Queued' : 'Not required' };
  if (table === 'table4') {
    if (action === 'bump') return { bumped: true };
    if (action === 'deprioritize') return { bumped: -1 };
    if (action === 'unbump') return { bumped: false };
    return { HealthCheck: 'Success', lastHealthCheckDate: Date.now() };
  }
  if (table === 'table5') return { HealthCheck: 'Queued' };
  return { HealthCheck: action === 'requeue' ? 'Queued' : 'Success' };
}

function stagedActions(item: TdarrStagedFile): ActivityAction[] {
  const base = [{ key: 'reset', label: 'Requeue' }];
  if (item.status === 'requireReview') return base.concat([{ key: 'reviewed', label: 'Reviewed' }, { key: 'skip', label: 'Skip' }, { key: 'fail', label: 'Fail' }]);
  if (item.status === 'transcodeSuccess' || item.status === 'conditionsMet') return base.concat([{ key: 'skip', label: 'Skip' }, { key: 'fail', label: 'Fail' }, { key: 'accept', label: 'Accept' }]);
  if (item.status === 'transcodeError' || item.status === 'transcodeCancelled') return base.concat([{ key: 'retry', label: 'Retry' }, { key: 'skip', label: 'Skip' }, { key: 'fail', label: 'Fail' }, ...(item.lastCompletedCacheFile?.file ? [{ key: 'accept', label: 'Accept' }] : [])]);
  return base;
}

function statusKind(status = '') {
  const value = status.toLowerCase();
  if (value.includes('success') || value.includes('healthy')) return kinds.SUCCESS;
  if (value.includes('error') || value.includes('cancel')) return kinds.DANGER;
  return kinds.WARNING;
}

function StagedRow({ item, isPending, showFullPaths, onAction }: { item: TdarrStagedFile; isPending: boolean; showFullPaths: boolean; onAction: (item: TdarrStagedFile, action: string) => void }) {
  const original = item.originalLibraryFile;
  const output = item.lastCompletedCacheFile;
  const saved = original?.file_size && output?.file_size ? (1 - output.file_size / original.file_size) * 100 : undefined;
  return <TableRow>
    <TableRowCell><Label kind={statusKind(item.status)}>{item.status || 'Processing'}</Label></TableRowCell>
    <TableRowCell className={showFullPaths ? undefined : 'tdarr-path'} title={original?.file}>{showFullPaths ? original?.file : fileName(original?.file)}</TableRowCell>
    <TableRowCell>{formatMegabytes(original?.file_size)}</TableRowCell><TableRowCell>{formatMegabytes(output?.file_size)}</TableRowCell>
    <TableRowCell>{saved === undefined ? '—' : `${saved.toFixed(1)}%`}</TableRowCell><TableRowCell>{item.workerGenus ?? '—'}</TableRowCell>
    <TableRowCell><ActivityActionMenu actions={stagedActions(item)} isSpinning={isPending} onAction={(action) => onAction(item, action)} /></TableRowCell>
  </TableRow>;
}

function MediaRow({ item, libraryNames, table = '', isPending, showFullPaths, onAction }: { item: TdarrMediaFile; libraryNames: Map<string, string>; table?: string; isPending: boolean; showFullPaths: boolean; onAction: (item: TdarrMediaFile, action: string) => void }) {
  return <TableRow>
    <TableRowCell>{libraryNames.get(item.DB ?? '') ?? item.DB ?? '—'}</TableRowCell>
    <TableRowCell className={showFullPaths ? undefined : 'tdarr-path'} title={item.file}>{showFullPaths ? item.file : fileName(item.file)}</TableRowCell>
    <TableRowCell>{item.video_codec_name ?? '—'}</TableRowCell><TableRowCell>{item.video_resolution ?? '—'}</TableRowCell>
    <TableRowCell>{item.audio_codec_name ?? '—'}</TableRowCell><TableRowCell>{item.container ?? '—'}</TableRowCell><TableRowCell>{formatMegabytes(item.file_size)}</TableRowCell>
    <TableRowCell><ActivityActionMenu actions={statusActions[table] ?? []} isSpinning={isPending} onAction={(action) => onAction(item, action)} /></TableRowCell>
  </TableRow>;
}

function HistoryRow({ item, isPending, showFullPaths, onAction }: { item: TdarrJobRecord; isPending: boolean; showFullPaths: boolean; onAction: (item: TdarrJobRecord) => void }) {
  return <TableRow>
    <TableRowCell>{item.job?.type ?? '—'}</TableRowCell><TableRowCell>{formatDate(item.start)}</TableRowCell>
    <TableRowCell className={showFullPaths ? undefined : 'tdarr-path'} title={item.file}>{showFullPaths ? item.file : fileName(item.file)}</TableRowCell><TableRowCell><Label kind={statusKind(item.status)}>{item.status || 'Running'}</Label></TableRowCell>
    <TableRowCell>{item.workerGenus ?? '—'}</TableRowCell><TableRowCell>{item.nodeNames?.join(', ') || '—'}</TableRowCell>
    <TableRowCell><ActivityActionMenu actions={[{ key: 'delete', label: 'Delete record' }]} isSpinning={isPending} onAction={() => onAction(item)} /></TableRowCell>
  </TableRow>;
}

export function ActivityPanel({ viewKey: suppliedViewKey, embedded = false, showFullPaths: suppliedShowFullPaths, filterAccessory }: { viewKey?: string; embedded?: boolean; showFullPaths?: boolean; filterAccessory?: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { view: routeViewKey = 'staging' } = useParams<{ view?: string }>();
  const viewKey = suppliedViewKey ?? routeViewKey;
  const view = views.find((item) => item.key === viewKey) ?? views[0];
  const showFullPaths = suppliedShowFullPaths ?? localStorage.getItem('showFullPaths') === 'yes';
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<string>();
  const [descending, setDescending] = useState(false);
  const [pendingAction, setPendingAction] = useState('');
  const [actionRequest, setActionRequest] = useState<ActionRequest | null>(null);
  const [actionMessage, setActionMessage] = useState<{ kind: 'success' | 'danger'; text: string } | null>(null);
  const libraries = useTdarrLibraries();
  const query = useTdarrActivityTable<TdarrMediaFile | TdarrStagedFile | TdarrJobRecord>(view.type, {
    page, pageSize, table: view.table, filterKey: view.type === 'staged' ? 'originalLibraryFile.file' : 'file', filterValue: filter, sortKey, sortDescending: descending
  });
  const libraryNames = useMemo(() => new Map(libraries.data?.map((library) => [library._id, library.name]) ?? []), [libraries.data]);
  const columns = view.type === 'staged' ? stagedColumns : view.type === 'jobs' ? historyColumns : mediaColumns;
  const total = query.data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const onSort = (key: string) => { setDescending(sortKey === key ? !descending : false); setSortKey(key); setPage(1); };
  useEffect(() => { setPage(1); setFilter(''); setSortKey(undefined); setDescending(false); setActionMessage(null); }, [view.key]);

  const runAction = useCallback(async (request: ActionRequest) => {
    setPendingAction(request.key); setActionMessage(null);
    try {
      await tdarrPost(request.path, { data: request.data });
      await queryClient.invalidateQueries({ queryKey: ['tdarr'] });
      setActionMessage({ kind: 'success', text: `${request.label} completed.` });
    } catch {
      setActionMessage({ kind: 'danger', text: `${request.label} failed. No local state was changed.` });
    } finally { setPendingAction(''); }
  }, [queryClient]);

  const requestAction = useCallback((request: ActionRequest) => request.confirmMessage ? setActionRequest(request) : void runAction(request), [runAction]);
  const onStatusAction = useCallback((item: TdarrMediaFile, action: string) => {
    if (action === 'delete') {
      requestAction({ key: `${item._id}:${action}`, label: 'Delete file', path: '/bulk-delete-files', data: { fileIds: [item._id] }, confirmMessage: `Delete ${item.file ?? item._id} from disk and Tdarr? This cannot be undone.` });
      return;
    }
    requestAction({ key: `${item._id}:${action}`, label: statusActions[view.table ?? '']?.find((candidate) => candidate.key === action)?.label ?? action, path: '/bulk-update-files', data: { fileIds: [item._id], updatedObj: getStatusUpdate(view.table ?? '', action) } });
  }, [requestAction, view.table]);
  const onStagedAction = useCallback((item: TdarrStagedFile, action: string) => requestAction({ key: `${item._id}:${action}`, label: stagedActions(item).find((candidate) => candidate.key === action)?.label ?? action, path: '/transcode-user-verdict', data: { obj: item, verdict: action } }), [requestAction]);
  const onHistoryAction = useCallback((item: TdarrJobRecord) => requestAction({ key: `${item._id}:delete`, label: 'Delete job record', path: '/delete-job-report', data: { jobId: item._id }, confirmMessage: 'Delete this job-history record? This cannot be undone.' }), [requestAction]);
  const panel = <>
        <div className="tdarr-activity-filters">
          <div className="tdarr-table-filter"><TextInput name="filter" value={filter} placeholder="Filter by file" onChange={({ value }) => { setFilter(String(value)); setPage(1); }} /></div>
          {filterAccessory}
        </div>
        {query.isError && <Alert kind={kinds.DANGER}>Unable to load this Tdarr activity table.</Alert>}
        {actionMessage && <Alert kind={actionMessage.kind}>{actionMessage.text}</Alert>}
        {query.isSuccess && <Table columns={columns} sortKey={sortKey} sortDirection={descending ? sortDirections.DESCENDING : sortDirections.ASCENDING} onSortPress={onSort}>
          <TableBody>
            {!query.data.array.length && <TableRow><TableRowCell colSpan={columns.filter((column) => column.isVisible).length}>No records found</TableRowCell></TableRow>}
            {query.data.array.map((item) => view.type === 'staged' ? <StagedRow key={item._id} item={item as TdarrStagedFile} isPending={pendingAction.startsWith(`${item._id}:`)} showFullPaths={showFullPaths} onAction={onStagedAction} /> : view.type === 'jobs' ? <HistoryRow key={item._id} item={item as TdarrJobRecord} isPending={pendingAction.startsWith(`${item._id}:`)} showFullPaths={showFullPaths} onAction={onHistoryAction} /> : <MediaRow key={item._id} item={item as TdarrMediaFile} libraryNames={libraryNames} table={view.table} isPending={pendingAction.startsWith(`${item._id}:`)} showFullPaths={showFullPaths} onAction={onStatusAction} />)}
          </TableBody>
        </Table>}
        {total > 0 && <div className="tdarr-pager"><span>{total} records · Page {page} of {totalPages}</span><div><Button size="small" isDisabled={page === 1} onPress={() => setPage((value) => Math.max(1, value - 1))}>Previous</Button>{' '}<Button size="small" isDisabled={page === totalPages} onPress={() => setPage((value) => Math.min(totalPages, value + 1))}>Next</Button></div></div>}
  </>;
  const confirm = <ConfirmModal isOpen={Boolean(actionRequest)} kind="danger" title={actionRequest?.label ?? 'Confirm action'} message={actionRequest?.confirmMessage ?? 'Continue?'} confirmLabel="Confirm" isSpinning={Boolean(pendingAction)} onConfirm={() => { if (actionRequest) { void runAction(actionRequest); setActionRequest(null); } }} onCancel={() => setActionRequest(null)} />;

  if (embedded) return <>{panel}{confirm}</>;

  return <PageContent title={view.label}>
    <PageToolbar><PageToolbarSection alignContent={align.LEFT}>
      <PageToolbarButton label="Refresh" iconName={icons.REFRESH} isSpinning={query.isFetching} onPress={() => query.refetch()} />
    </PageToolbarSection></PageToolbar>
    <PageContentBody><FieldSet legend={view.label}>{panel}</FieldSet></PageContentBody>
    {confirm}
  </PageContent>;
}

export default function Activity() {
  return <ActivityPanel />;
}
